/**
 * Buscar un producto de la tienda y agregarlo a la cotizacion (COT-01).
 *
 * Usa el mismo buscador de la tabla de Productos, asi que encuentra por nombre,
 * codigo o marca, y tambien los ocultos (pueden estar esperando sus fotos). Un
 * producto sin precio se muestra pero no se puede agregar: se cotiza con el
 * precio de la tienda, y ese precio no existe todavia.
 */
import { useEffect, useId, useState } from 'react';

import type { ProductoAdminResumen } from '@bandu/compartido';

import { usePeticion } from '../../hooks/use-peticion';
import { obtenerProductos } from '../../servicios/administracion.servicio';
import { numero, pesos } from '../../utilidades/formato';
import { nombreConVariante } from '../../utilidades/producto';
import { AvisoError } from '../comunes/EstadoPeticion';
import { Icono } from '../comunes/Icono';
import { Imagen } from '../comunes/Imagen';

const ESPERA_TECLEO_MS = 250;
const RESULTADOS = 8;
const LARGO_MINIMO = 2;

export interface BuscadorProductoCotizacionProps {
  /** Los que ya estan en la cotizacion: se muestran como agregados. */
  agregados: ReadonlySet<number>;
  onAgregar(producto: ProductoAdminResumen): void;
}

function sinBusqueda(): Promise<null> {
  return Promise.resolve(null);
}

export function BuscadorProductoCotizacion({ agregados, onAgregar }: BuscadorProductoCotizacionProps) {
  const id = useId();
  const [texto, setTexto] = useState('');
  const [termino, setTermino] = useState('');

  // Espera a que se deje de escribir: una consulta por palabra, no por tecla.
  useEffect(() => {
    const espera = window.setTimeout(() => {
      setTermino(texto.trim());
    }, ESPERA_TECLEO_MS);
    return () => {
      window.clearTimeout(espera);
    };
  }, [texto]);

  const buscando = termino.length >= LARGO_MINIMO;
  const { datos, error, estado } = usePeticion(
    (senal) =>
      buscando ? obtenerProductos({ termino, porPagina: RESULTADOS }, senal) : sinBusqueda(),
    [termino],
  );
  const productos = datos?.items ?? [];

  return (
    <div className="buscador-cotizacion">
      <div className="campo">
        <label htmlFor={`${id}-buscar`}>Buscar un producto</label>
        <input
          id={`${id}-buscar`}
          type="search"
          placeholder="Nombre, código o marca"
          autoComplete="off"
          value={texto}
          onChange={(evento) => {
            setTexto(evento.target.value);
          }}
        />
      </div>

      {estado === 'error' ? <AvisoError error={error} /> : null}

      <p className="solo-lectores" role="status">
        {buscando && datos !== null ? `${numero(productos.length)} productos encontrados` : ''}
      </p>

      {buscando && datos !== null && productos.length === 0 ? (
        <p className="panel-vacio">No hay productos con «{termino}».</p>
      ) : null}

      {productos.length > 0 ? (
        <ul className="resultados-cotizacion">
          {productos.map((producto) => {
            const agregado = agregados.has(producto.id);
            const sinPrecio = producto.precio <= 0;
            return (
              <li key={producto.id} className="resultado-cotizacion">
                {producto.imagen !== null ? (
                  <Imagen
                    className="mini-img"
                    src={producto.imagen.url}
                    alt=""
                    ancho={producto.imagen.ancho}
                    alto={producto.imagen.alto}
                  />
                ) : (
                  <span className="mini-img sin-foto" aria-hidden="true" />
                )}
                <div className="item-pedido-nombre">
                  <span className="n">{nombreConVariante(producto)}</span>
                  <span className="s mono">{producto.sku}</span>
                  <span className="s">
                    {sinPrecio ? 'Sin precio' : pesos(producto.precio)} · Stock {numero(producto.stock)}
                  </span>
                </div>
                <button
                  type="button"
                  className="btn btn-xs btn-contorno"
                  disabled={agregado || sinPrecio}
                  onClick={() => {
                    onAgregar(producto);
                  }}
                >
                  {agregado ? (
                    <>
                      <Icono nombre="check" />
                      Agregado
                    </>
                  ) : (
                    <>
                      <Icono nombre="mas" />
                      Agregar
                      <span className="solo-lectores"> {nombreConVariante(producto)}</span>
                    </>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
